import { createFileRoute } from "@tanstack/react-router";
import React, { useEffect, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ChevronDownIcon,
  HistoryIcon,
  MessageSquarePlus,
  PlusIcon,
  Settings2Icon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import type {
  Chat,
  ChatFiles,
  ChatHistory,
  Entity,
  EntityChange,
  LLM,
} from "@/lib/types/secure-llm";
import Body from "@/components/secure-llm/Body";
import Footer from "@/components/secure-llm/Footer";
import { useEntities } from "@/lib/queries/secure-llm/getEntities";
import { useLlms } from "@/lib/queries/secure-llm/getLlms";
import { useMyChats } from "@/lib/queries/secure-llm/getMyChats";
import { api } from "@/lib/http";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { PiiTagGroup } from "@/components/secure-llm/PiiTag";
import { PiiSelector } from "@/components/secure-llm/PiiSelector";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "@/lib/hooks/mocks";

// Custom hook for handling outside clicks
const useOutsideClick = (callback: () => void) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [callback]);

  return ref;
};

// LocalStorage key for selected entities
const SELECTED_ENTITIES_STORAGE_KEY = "polygraf_selected_entities";

export const Route = createFileRoute("/_app/secure-llm")({
  component: RouteComponent,
  staticData: {
    title: "Secure LLM Chat",
  },
  head: () => ({
    meta: [
      {
        name: "description",
        content:
          "Keep your AI chats private with Polygraf AI’s Secure LLM Chat - local or on-premises deployments, end-to-end encrypted conversations, detailed audit logs, and zero-trust governance for mission-critical workflows.",
      },
    ],
  }),
});

function RouteComponent() {
  const queryClient = useQueryClient();

  const chatBoxRef = useRef<HTMLDivElement | null>(null);

  // Queries using TanStack Query
  const entitiesQuery = useEntities();
  const llmsQuery = useLlms();
  const myChatsQuery = useMyChats();

  // Derived state from queries
  const entities = entitiesQuery.data || [];
  const llms = llmsQuery.data || [];
  const chats = myChatsQuery.data?.items || [];

  // Local state
  const [loading, setLoading] = useState<boolean>(false);
  const [isConversationLoading, setIsConversationLoading] =
    useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<Array<ChatHistory>>(() => [
    {
      id: "a4dfbf99-9a51-4141-99b7-7d653fbf959b",
      text: "Hi, I need to draft an email to our client, [Company 1], about the new product launch. The contact is [Person 1], her email is [Email 1], and her phone is [Phone Number 1]. Can you help me?",
      file_path: null,
      occurred_entities: [
        {
          original_text: "Acme Corp",
          modified_text: "[Company 1]",
          entity_name: "Company",
          existing: false,
        },
        {
          original_text: "Jane Smith",
          modified_text: "[Person 1]",
          entity_name: "Person",
          existing: false,
        },
        {
          original_text: "jane.smith@acme.com",
          modified_text: "[Email 1]",
          entity_name: "Email",
          existing: false,
        },
        {
          original_text: "+1-202-555-0123",
          modified_text: "[Phone Number 1]",
          entity_name: "Phone Number",
          existing: false,
        },
      ],
      created_at: "2025-10-14T11:47:27.413564",
      texted_by: "user",
    },
    {
      id: "f6cceef4-bd94-4948-9ec4-bbafbc49e608",
      text: "Certainly! Here's a draft for your email:\n\n---\n\nSubject: Exciting News: Launching Our New Product!\n\nHi [Person 1],\n\nI hope this message finds you well. We are thrilled to announce the upcoming launch of our new product, and I wanted to share the details with you firsthand.\n\nThis innovative product (briefly describe the product, e.g., \"aims to enhance your team's productivity by streamlining your workflow processes\") and has been developed with feedback and insights from valued clients like [Company 1].\n\n(Optional: Include any details about a launch event, special offers, or exclusive previews)\n\nWe believe that [Company 1] will greatly benefit from the enhancements this product offers, and we’re eager to hear your thoughts.\n\nFeel free to reach out via email at [Email 1] or phone at [Phone Number 1] if you have any questions or if you would like to schedule a demo.\n\nThank you for your continued partnership. We are excited to support [Company 1] with this new offering.\n\nBest regards,\n\n(Your Full Name)\n(Your Position)\n(Your Company Name)\n(Your Phone Number)\n(Your Email Address)\n\n---\n\nMake sure to fill in any specific details about the product and any additional information that may be relevant or beneficial to the client.",
      file_path: null,
      occurred_entities: [],
      created_at: "2025-10-14T11:47:33.558984",
      texted_by: "assistant",
    },
    {
      id: "0ad5d326-04cf-42df-a279-2f2d3a36ac77",
      text: "Could you also include some more info: the launch date is [Date/Time 1], [Date/Time 2]-[Date/Time 3] pricing is [Currency 1][Amount 1] per unit, and the project code is Project Phoenix.\n",
      file_path: null,
      occurred_entities: [
        {
          original_text: "October 28",
          modified_text: "[Date/Time 1]",
          entity_name: "Date/Time",
          existing: false,
        },
        {
          original_text: "early",
          modified_text: "[Date/Time 2]",
          entity_name: "Date/Time",
          existing: false,
        },
        {
          original_text: "bird",
          modified_text: "[Date/Time 3]",
          entity_name: "Date/Time",
          existing: false,
        },
        {
          original_text: "$",
          modified_text: "[Currency 1]",
          entity_name: "Currency",
          existing: false,
        },
        {
          original_text: "499",
          modified_text: "[Amount 1]",
          entity_name: "Amount",
          existing: false,
        },
      ],
      created_at: "2025-10-14T11:49:37.913225",
      texted_by: "user",
    },
    {
      id: "cab09775-ac7d-4b16-846b-6076be107366",
      text: "Certainly! Here's an updated draft with the additional details:\n\n---\n\nSubject: Exciting News: Launching Our New Product!\n\nHi [Person 1],\n\nI hope this message finds you well. We are thrilled to announce the upcoming launch of our new product under Project Phoenix, and I wanted to share the details with you firsthand.\n\n**Launch Details:**\n- **Launch Date:** [Date/Time 1]\n- **Pricing:** [Currency 1][Amount 1] per unit\n\nThis innovative product (briefly describe the product, e.g., \"aims to enhance your team's productivity by streamlining your workflow processes\") and has been developed with feedback and insights from valued clients like [Company 1].\n\nTo celebrate, we are hosting a special launch event on [Date/Time 2] to [Date/Time 3], and we’d love for you and your team to join us. During the event, you’ll have the opportunity to see the product in action and discuss how it can benefit [Company 1].\n\nWe believe that [Company 1] will greatly benefit from the enhancements this product offers, and we’re eager to hear your thoughts.\n\nFeel free to reach out via email at [Email 1] or phone at [Phone Number 1] if you have any questions or if you would like to schedule a demo before the official launch.\n\nThank you for your continued partnership. We are excited to support [Company 1] with this new offering.\n\nBest regards,\n\n(Your Full Name)\n(Your Position)\n(Your Company Name)\n(Your Phone Number)\n(Your Email Address)\n\n---\n\nPlease adjust any specific product descriptions or additional information as needed.",
      file_path: null,
      occurred_entities: [],
      created_at: "2025-10-14T11:49:41.501811",
      texted_by: "assistant",
    },
    {
      id: "20d0958a-f75f-4b23-beb9-37447935f951",
      text: "Great! Also, can you remind her that the shipping address for the first shipment is [Address 1] [Address 2], [Location 1], [Location 2] [Zipcode 1], and include our internal account number [Account Number 1]?",
      file_path: null,
      occurred_entities: [
        {
          original_text: "123",
          modified_text: "[Address 1]",
          entity_name: "Address",
          existing: false,
        },
        {
          original_text: "Main St",
          modified_text: "[Address 2]",
          entity_name: "Address",
          existing: false,
        },
        {
          original_text: "Springfield",
          modified_text: "[Location 1]",
          entity_name: "Location",
          existing: false,
        },
        {
          original_text: "IL",
          modified_text: "[Location 2]",
          entity_name: "Location",
          existing: false,
        },
        {
          original_text: "62704",
          modified_text: "[Zipcode 1]",
          entity_name: "Zipcode",
          existing: false,
        },
        {
          original_text: "987654321",
          modified_text: "[Account Number 1]",
          entity_name: "Account Number",
          existing: false,
        },
      ],
      created_at: "2025-10-14T11:49:56.517306",
      texted_by: "user",
    },
    {
      id: "65aa324d-12c2-4103-9405-d57f7d234b89",
      text: "Certainly! Here's the updated email draft with the additional details regarding the shipping address and account number:\n\n---\n\nSubject: Exciting News: Launching Our New Product!\n\nHi [Person 1],\n\nI hope this message finds you well. We are thrilled to announce the upcoming launch of our new product under Project Phoenix, and I wanted to share the details with you firsthand.\n\n**Launch Details:**\n- **Launch Date:** [Date/Time 1]\n- **Pricing:** [Currency 1][Amount 1] per unit\n\nThis innovative product (briefly describe the product, e.g., \"aims to enhance your team's productivity by streamlining your workflow processes\") and has been developed with feedback and insights from valued clients like [Company 1].\n\nTo celebrate, we are hosting a special launch event on [Date/Time 2] to [Date/Time 3], and we’d love for you and your team to join us. During the event, you’ll have the opportunity to see the product in action and discuss how it can benefit [Company 1].\n\nAdditionally, I wanted to confirm the shipping address for the first shipment:\n[Address 1] [Address 2]\n[Location 1], [Location 2] [Zipcode 1]\n\nOur internal account number for this transaction is [Account Number 1]. Please feel free to reach out if you need to update or verify any of these details.\n\nWe believe that [Company 1] will greatly benefit from the enhancements this product offers, and we’re eager to hear your thoughts.\n\nFeel free to contact me at [Email 1] or [Phone Number 1] if you have any questions or if you would like to schedule a demo before the official launch.\n\nThank you for your continued partnership. We are excited to support [Company 1] with this new offering.\n\nBest regards,\n\n(Your Full Name)\n(Your Position)\n(Your Company Name)\n(Your Phone Number)\n(Your Email Address)\n\n---\n\nAdjust any specific product details or additional information as needed.",
      file_path: null,
      occurred_entities: [],
      created_at: "2025-10-14T11:50:02.504453",
      texted_by: "assistant",
    },
  ]);

  const [chatFiles, setChatFiles] = useState<Array<ChatFiles>>([]);
  const [selectedFiles, setSelectedFiles] = useState<Array<ChatFiles>>([]);
  const [viewMode, setViewMode] = React.useState<"ai" | "human">("ai");

  // actions
  const [currentChatId, setCurrentChatId] = useState<string>(() =>
    crypto.randomUUID(),
  );
  const [selectedEntities, setSelectedEntities] = useState<Array<Entity>>([
    { id: "FFFFFFFF-FFFF-FFFF-FFFF-FFFFFFFFFFFF", name: "Person" },
  ]);
  const [occuredEntities, setOccuredEntities] = useState<
    Array<{
      original_text: string;
      modified_text: string;
      entity: Entity;
    }>
  >([
    {
      original_text: "+1-202-555-0123",
      modified_text: "[Phone Number 1]",
      entity: {
        id: "03c6ea6c-6027-44ce-9f77-9b75098fe26d",
        name: "Phone Number",
      },
    },
    {
      original_text: "Main St",
      modified_text: "[Address 2]",
      entity: {
        id: "0a495de7-3be0-48fd-988d-9c751d0e7a6f",
        name: "Address",
      },
    },
    {
      original_text: "123",
      modified_text: "[Address 1]",
      entity: {
        id: "0a495de7-3be0-48fd-988d-9c751d0e7a6f",
        name: "Address",
      },
    },
    {
      original_text: "62704",
      modified_text: "[Zipcode 1]",
      entity: {
        id: "3e178c15-66d0-4080-b3a2-c906a620c14e",
        name: "Zipcode",
      },
    },
    {
      original_text: "jane.smith@acme.com",
      modified_text: "[Email 1]",
      entity: {
        id: "417beefa-6f7b-4fcb-ab87-33c1d58d560a",
        name: "Email",
      },
    },
    {
      original_text: "$",
      modified_text: "[Currency 1]",
      entity: {
        id: "55e870a7-8b7b-420f-a2d7-d89dec5a7922",
        name: "Currency",
      },
    },
    {
      original_text: "Acme Corp",
      modified_text: "[Company 1]",
      entity: {
        id: "6c954141-8124-4d96-be80-ab827e182c3f",
        name: "Company",
      },
    },
    {
      original_text: "bird",
      modified_text: "[Date/Time 3]",
      entity: {
        id: "75e92059-6c8e-429a-8c3d-2d6a4a7951cf",
        name: "Date/Time",
      },
    },
    {
      original_text: "October 28",
      modified_text: "[Date/Time 1]",
      entity: {
        id: "75e92059-6c8e-429a-8c3d-2d6a4a7951cf",
        name: "Date/Time",
      },
    },
    {
      original_text: "early",
      modified_text: "[Date/Time 2]",
      entity: {
        id: "75e92059-6c8e-429a-8c3d-2d6a4a7951cf",
        name: "Date/Time",
      },
    },
    {
      original_text: "987654321",
      modified_text: "[Account Number 1]",
      entity: {
        id: "8215b32c-e83e-40f4-bcd0-c7d377e51a3b",
        name: "Account Number",
      },
    },
    {
      original_text: "Springfield",
      modified_text: "[Location 1]",
      entity: {
        id: "82867343-8da9-4655-9caf-1b0a924487b0",
        name: "Location",
      },
    },
    {
      original_text: "IL",
      modified_text: "[Location 2]",
      entity: {
        id: "82867343-8da9-4655-9caf-1b0a924487b0",
        name: "Location",
      },
    },
    {
      original_text: "499",
      modified_text: "[Amount 1]",
      entity: {
        id: "87484231-bee7-4adf-b73f-5fc868260daa",
        name: "Amount",
      },
    },
    {
      original_text: "Jane Smith",
      modified_text: "[Person 1]",
      entity: {
        id: "f68b91fd-bdc3-4603-8fb1-9a89e4ae71e0",
        name: "Person",
      },
    },
  ]);

  const [selectedModel, setSelectedModel] = useState<LLM>({
    id: "1dfbab85-b7ec-458e-9371-ec50fedc6b53",
    name: "GPT-4o",
  });

  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [piiSelectorOpen, setPiiSelectorOpen] = useState(false);

  const [redacted, setRedacted] = useState(false);
  const [redactedText, setRedactedText] = useState("");
  const [changedValues, setChangedValues] = useState<Array<EntityChange>>([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Mobile popover states
  const [historyOpen, setHistoryOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Refs for dropdown containers
  const modelDropdownRef = useOutsideClick(() => setModelDropdownOpen(false));

  const piiSelectorRef = useOutsideClick(() => setPiiSelectorOpen(false));

  // Function to close all dropdowns
  const closeAllDropdowns = () => {
    setModelDropdownOpen(false);
    setPiiSelectorOpen(false);
  };

  // Modified dropdown toggle functions
  const toggleModelDropdown = () => {
    closeAllDropdowns();
    setModelDropdownOpen((prev) => !prev);
  };

  const togglePiiSelectorDropdown = () => {
    closeAllDropdowns();
    setPiiSelectorOpen((prev) => !prev);
  };

  // Load selectedEntities from localStorage when component mounts
  useEffect(() => {
    try {
      const savedEntities = localStorage.getItem(SELECTED_ENTITIES_STORAGE_KEY);
      if (savedEntities) {
        const parsedEntities = JSON.parse(savedEntities) as Array<Entity>;
        if (Array.isArray(parsedEntities) && parsedEntities.length > 0) {
          setSelectedEntities(parsedEntities);
        }
      }
    } catch (error) {
      console.error(
        "Failed to load selected entities from localStorage:",
        error,
      );
    }
  }, []);

  // Save selectedEntities to localStorage whenever it changes
  useEffect(() => {
    if (selectedEntities.length > 0) {
      try {
        localStorage.setItem(
          SELECTED_ENTITIES_STORAGE_KEY,
          JSON.stringify(selectedEntities),
        );
      } catch (error) {
        console.error(
          "Failed to save selected entities to localStorage:",
          error,
        );
      }
    }
  }, [selectedEntities]);

  const parseLocalStorageEntities = () => {
    const savedEntities = localStorage.getItem(SELECTED_ENTITIES_STORAGE_KEY);
    if (savedEntities) {
      try {
        const parsedEntities = JSON.parse(savedEntities) as Array<Entity>;
        if (Array.isArray(parsedEntities) && parsedEntities.length > 0) {
          return parsedEntities;
        }
      } catch (error) {
        localStorage.removeItem(SELECTED_ENTITIES_STORAGE_KEY);
        console.error(
          "Failed to parse selected entities from localStorage:",
          error,
        );
      }
    }
    return [];
  };

  useEffect(() => {
    // if localStorage some value doesn't exist on entities remove it
    if (localStorage.getItem(SELECTED_ENTITIES_STORAGE_KEY)) {
      const savedEntities = localStorage.getItem(SELECTED_ENTITIES_STORAGE_KEY);
      if (savedEntities) {
        // try to parse the entities
        const parsedEntities = parseLocalStorageEntities();
        if (Array.isArray(parsedEntities) && parsedEntities.length > 0) {
          setSelectedEntities(
            parsedEntities.filter((entity) =>
              entities.some((e) => e.id === entity.id),
            ),
          );
        }
      }
    }
    // Set default entities when entities are loaded
    if (
      entities.length > 0 &&
      selectedEntities.length === 0 &&
      !localStorage.getItem(SELECTED_ENTITIES_STORAGE_KEY)
    ) {
      setSelectedEntities([...entities]);
    }
  }, [entities]);

  const addFileToChatMutation = useMutation({
    mutationFn: async ({
      formData,
    }: {
      formData: FormData;
      fileName: string;
    }) => {
      const response = await api.governance.post(
        `${currentChatId}/file-upload?llm_id=${selectedModel.id}`,
        { body: formData },
      );
      return await response.json();
    },
    onSuccess: (data: any) => {
      const newFile = { id: data.id, file_path: data.file_path };
      setChatFiles((prevState) => [newFile, ...prevState]);
      setSelectedFiles((prevState) => [newFile, ...prevState]);

      // Add file to chat history
      setChatHistory((prevState) => [
        ...prevState,
        {
          id: crypto.randomUUID(),
          text: "",
          created_at: "",
          file_path: data.file_path,
          occurred_entities: [],
          texted_by: "user",
        },
      ]);

      // Scroll to bottom of chat
      setTimeout(() => {
        chatBoxRef.current?.scrollTo({
          top: chatBoxRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    },
  });

  const deleteChatFileMutation = useMutation({
    mutationFn: async (id: string) => {
      return await api.governance.delete(`messages/${id}`);
    },
    onSuccess: (_, id) => {
      setChatFiles((prevState) => prevState.filter((e) => e.id !== id));
      setSelectedFiles((prevState) => prevState.filter((e) => e.id !== id));
    },
  });

  const redactTextMutation = useMutation({
    mutationFn: async () => {
      const isNewEmptyChat =
        !chats.some((chat: Chat) => chat.id === currentChatId) ||
        (chats.some(
          (chat: Chat) =>
            chat.id === currentChatId && chat.title.includes("New chat"),
        ) &&
          chatHistory.length === 0);

      const endpoint = isNewEmptyChat
        ? "modify-pid"
        : `modify-pid?chat_id=${currentChatId}`;

      const response = await api.governance.post(endpoint, {
        json: {
          text: currentMessage,
          user_entity_list: selectedEntities.map((a) => a.name),
        },
      });
      return await response.json();
    },
    onSuccess: (data: any) => {
      setRedacted(true);
      setRedactedText(data.replaced_text);
      setChangedValues(data.entities);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: async ({
      originalText,
      modifiedText,
    }: {
      originalText: string;
      modifiedText: string;
    }) => {
      const selectedFileIds = new Set(selectedFiles.map((file) => file.id));
      const blackListFiles = chatFiles
        .filter((file) => !selectedFileIds.has(file.id))
        .map((file) => file.id);

      const response = await api.governance.post(
        `${currentChatId}/message?llm_id=${selectedModel.id}`,
        {
          json: {
            original_text: originalText,
            modified_text: modifiedText,
            entities: changedValues,
            blacklist_file_ids: blackListFiles,
          },
        },
      );
      return await response.json();
    },
    onSuccess: (data: any) => {
      setChatHistory((prevState) => [
        ...prevState,
        {
          id: crypto.randomUUID(),
          text: data.text,
          created_at: "",
          file_path: null,
          occurred_entities: data.occurred_entities || [],
          texted_by: "assistant",
        },
      ]);
      setChangedValues([]);
      setTimeout(() => {
        chatBoxRef.current?.scrollTo({
          top: chatBoxRef.current.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    },
    onError: (error: any) => {
      console.error("Failed to send message:", error);
      setLoading(false);
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  // open new Chat
  const openNewChat = () => {
    const existingNewChat = chats.find((e: Chat) =>
      e.title.includes("New chat"),
    );

    if (existingNewChat) {
      // If a "New chat" exists and it's not the current chat, navigate to it
      if (existingNewChat.id !== currentChatId) {
        setCurrentChatId(existingNewChat.id);
        setCurrentMessage("");
        setRedacted(false);
        setRedactedText("");
        setError(null);
        setOccuredEntities([]);
      }
    } else {
      // If no "New chat" exists, create one
      setCurrentMessage("");
      setRedacted(false);
      setRedactedText("");
      setError(null);
      setChatHistory([]);
      setOccuredEntities([]);
      const newIndex = crypto.randomUUID();
      // Don't add to query data yet - we'll add it when there's content
      setCurrentChatId(newIndex);
      setChatFiles([]);
      setSelectedFiles([]);
    }
  };

  // get chat entities
  const getChatEntities = async (chat_id: string) => {
    try {
      const response = await api.governance.get<
        Array<{
          original_text: string;
          modified_text: string;
          entity: Entity;
        }>
      >(`my-chats/${chat_id}/occurred-entities`);
      const data = await response.json();
      setOccuredEntities(data);
    } catch (error) {
      setOccuredEntities([]);
    }
  };

  // get Chat files
  const getChatFiles = async (chat_id: string) => {
    try {
      const response = await api.governance.get<{ items: Array<ChatFiles> }>(
        `${chat_id}/chat-history?only_files=true&per_page=100`,
      );
      const data = await response.json();
      setChatFiles(data.items);
      setSelectedFiles(data.items);
    } catch (error) {
      setChatFiles([]);
      setSelectedFiles([]);
    }
  };

  // add file to Chat
  const addFileToChat = async (file: File, fileName: string) => {
    setLoading(true);
    setError(null);

    if (!selectedModel.id) {
      setError("Please select a model before uploading files");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      await addFileToChatMutation.mutateAsync({ formData, fileName });

      if (!chats.some((e: Chat) => e.id === currentChatId)) {
        queryClient.setQueryData(
          ["secure-llm", "my-chats"],
          (oldData: any = { items: [] }) => ({
            ...oldData,
            items: [
              { id: currentChatId, title: fileName },
              ...(oldData.items || []),
            ],
          }),
        );
      }
    } catch (error) {
      console.error("Failed to add file:", error);
      setError("Failed to upload file");
    } finally {
      setLoading(false);
    }
  };

  // delete Chat File
  const deleteChatFile = async (id: string) => {
    try {
      await deleteChatFileMutation.mutateAsync(id);
    } catch (error) {
      console.error("Failed to delete file:", error);
    }
  };

  // select Chat
  const selectChat = (e: ChatFiles) => {
    setSelectedFiles((prevSelectedFiles) => {
      const isSelected = prevSelectedFiles.some((file) => file.id === e.id);
      if (isSelected) {
        return prevSelectedFiles.filter((file) => file.id !== e.id);
      }
      return [...prevSelectedFiles, e];
    });
  };

  // Load chat history and files when current chat ID changes
  useEffect(() => {
    // Check if this is a new chat
    const isNewChat =
      !chats.some((chat: Chat) => chat.id === currentChatId) ||
      chats.some(
        (chat: Chat) =>
          chat.id === currentChatId && chat.title.includes("New chat"),
      );

    if (isNewChat) {
      // For new chats, just reset states without loading
      setChatHistory([]);
      setChatFiles([]);
      setSelectedFiles([]);
      setCurrentMessage("");
      setRedacted(false);
      setRedactedText("");
      setError(null);
      setOccuredEntities([]);
      return;
    }

    // Only load history and files for existing chats
    setIsConversationLoading(true);
    setOccuredEntities([]);
    getChatFiles(currentChatId);
    getChatEntities(currentChatId);
    setError(null);
    setCurrentMessage("");
    setRedacted(false);
    setRedactedText("");
  }, [currentChatId]);

  // redact text (modify text)
  const handleTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentMessage(e.target.value);
  };

  const redactText = async () => {
    setError(null);

    if (currentMessage.length === 0) {
      setError("Prompt should be minimum 1 symbol");
      return;
    }

    if (selectedEntities.length === 0) {
      setError("Please select at least one PII type to filter");
      return;
    }

    setLoading(true);
    try {
      await redactTextMutation.mutateAsync();
    } catch (error) {
      console.error("Failed to redact text:", error);
      setError("Failed to redact text");
      setLoading(false);
    }
  };

  // send text after modify
  const sendText = async () => {
    if (!selectedModel.id) {
      setError("Please select a model before sending message");
      return;
    }

    setLoading(true);
    const check = chats.find((e: Chat) => e.id === currentChatId);
    if (!check) {
      queryClient.setQueryData(
        ["secure-llm", "my-chats"],
        (oldData: any = { items: [] }) => ({
          ...oldData,
          items: [
            ...(oldData.items || []),
            {
              id: currentChatId,
              title: redactedText.substring(0, 40),
            },
          ],
        }),
      );
    } else if (check.id === currentChatId && check.title.includes("New chat")) {
      queryClient.setQueryData(
        ["secure-llm", "my-chats"],
        (oldData: any = { items: [] }) => ({
          ...oldData,
          items: oldData.items.map((e: Chat) => {
            if (e.id === currentChatId) {
              return { ...e, title: redactedText.substring(0, 40) };
            }
            return e;
          }),
        }),
      );
    } else if (chats.length === 0) {
      queryClient.setQueryData(["secure-llm", "my-chats"], {
        items: [{ id: currentChatId, title: redactedText.substring(0, 40) }],
      });
    }

    setChatHistory((prevState) => [
      ...prevState,
      {
        id: crypto.randomUUID(),
        text: redactedText,
        created_at: "",
        file_path: null,
        occurred_entities: changedValues.map((entity) => ({
          original_text: entity.original_text,
          modified_text: entity.modified_text,
          entity_name: entity.entity_name,
          existing: entity.existing === "true",
        })),
        texted_by: "user",
      },
    ]);

    const curr_msg = currentMessage;
    const mod_txt = redactedText;

    setRedactedText("");
    setCurrentMessage("");
    setRedacted(false);
    setTimeout(() => {
      chatBoxRef.current?.scrollTo({
        top: chatBoxRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 100);

    try {
      await sendMessageMutation.mutateAsync({
        originalText: curr_msg,
        modifiedText: mod_txt,
      });
    } catch (error) {
      console.error("Failed to send message:", error);
      setLoading(false);
    }
    await getChatEntities(currentChatId);
  };

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === "ai" ? "human" : "ai"));
  };

  // Render history content
  const renderHistoryContent = () => (
    <div className="w-full overflow-y-auto">
      <div className="mb-4">
        <p className="text-sm font-medium text-gray-500">Chat History</p>
      </div>
      <ul className="space-y-3">
        {chats.map((item) => (
          <li
            key={item.id}
            className={`cursor-pointer rounded-lg border bg-white p-3 hover:bg-gray-100 dark:border-gray-700 dark:bg-black dark:text-white dark:hover:bg-gray-800 ${
              currentChatId === item.id ? "border-pink-500" : ""
            }`}
            onClick={() => {
              setCurrentChatId(item.id);
              setHistoryOpen(false);
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-sm font-medium">{item.title}</span>
                <span className="mt-1 text-xs text-gray-500">
                  {item.created_at &&
                    new Date(item.created_at).toLocaleString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                </span>
              </div>
              <div className="flex flex-col-reverse items-center gap-2">
                {item.id === currentChatId && (
                  <span className="mr-2 rounded-md bg-pink-100 px-2 py-0.5 text-xs font-medium text-pink-800">
                    Current
                  </span>
                )}
                {/* Hide delete button for new empty chats
                {!(
                  item.title.includes("New chat") &&
                  item.id === currentChatId &&
                  chatHistory.length === 0
                ) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      confirmDeleteChat(item.id);
                    }}
                    className="cursor-pointer hover:opacity-70"
                    disabled={deletingChats.includes(item.id)}
                  >
                    {deletingChats.includes(item.id) ? (
                      <Loader2Icon className="h-4 w-4 animate-spin" />
                    ) : (
                      <TrashIcon className="h-4 w-4" />
                    )}
                  </button>
                )} */}
              </div>
            </div>
          </li>
        ))}
      </ul>
      <div className="mt-6">
        <p className="mb-2 text-sm font-medium text-gray-500">Your documents</p>
        {loading && chatFiles.length === 0 && (
          <div className="flex items-center justify-center py-4 text-sm text-gray-500">
            Loading documents...
          </div>
        )}
        {chatFiles.length === 0 && !loading && (
          <div className="py-4 text-center text-sm text-gray-500">
            No documents uploaded yet
          </div>
        )}
        <ul className="space-y-2">
          {chatFiles.map((item) => (
            <li
              key={item.id}
              className="flex items-center justify-between rounded-md bg-gray-50 p-2 dark:bg-gray-800"
            >
              <span className="mr-2 truncate text-sm">{item.file_path}</span>

              <div className="flex items-center gap-2">
                <Switch
                  checked={selectedFiles.some(
                    (e: ChatFiles) => e.id == item.id,
                  )}
                  onCheckedChange={() => selectChat(item)}
                />
                <button
                  onClick={() => deleteChatFile(item.id)}
                  className="cursor-pointer hover:opacity-70"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  // Render preferences content
  const renderPreferencesContent = () => (
    <div className="relative w-full overflow-y-auto">
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-500">LLM Model</h3>
          <Button
            variant="outline"
            className="flex items-center gap-1.5 border-pink-500 text-pink-500 hover:border-pink-500 hover:text-pink-500 dark:border-pink-500 dark:text-pink-500 dark:hover:border-pink-500 dark:hover:text-pink-500"
            onClick={() => {}}
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add LLM API Key
          </Button>
        </div>
        <div className="relative" ref={modelDropdownRef}>
          <button
            className="flex w-full items-center justify-between rounded-md border border-gray-200 bg-white p-2.5 text-left dark:border-gray-700 dark:bg-black dark:text-white"
            onClick={toggleModelDropdown}
          >
            <span>{selectedModel.name || "Select a model"}</span>
            <ChevronDownIcon className="h-4 w-4" />
          </button>
          {modelDropdownOpen && (
            <div className="absolute top-full right-0 left-0 z-10 mt-1 flex flex-col gap-2 overflow-hidden rounded-md border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-black dark:text-white">
              {llms.map((model) => {
                const isAvailable = model.is_free;
                const isSelected = selectedModel.id === model.id;

                return (
                  <div
                    key={model.id}
                    className={`flex cursor-pointer items-center justify-between rounded px-3 py-2 ${
                      isAvailable
                        ? "hover:bg-gray-300 dark:hover:bg-gray-800"
                        : "cursor-not-allowed opacity-50"
                    } ${isSelected ? "bg-gray-200 dark:bg-gray-800" : ""}`}
                    onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                      e.stopPropagation();
                      if (isAvailable) {
                        setSelectedModel(model);
                        setModelDropdownOpen(false);
                      }
                    }}
                  >
                    <span>{model.name}</span>
                    <div className="flex items-center">
                      {!isAvailable && (
                        <span className="mr-2 text-xs text-gray-500">
                          Locked
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {!selectedModel.id && (
          <p className="mt-2 text-sm text-red-500">
            Please select a model to continue
          </p>
        )}
      </div>

      <div className="mb-6">
        <h3 className="mb-2 text-sm font-medium text-gray-500">PII Types</h3>

        <div ref={piiSelectorRef} className="relative min-h-65">
          <PiiSelector
            entities={entities}
            selectedEntities={selectedEntities}
            onChange={setSelectedEntities}
            isDropdownOpen={piiSelectorOpen}
            setIsDropdownOpen={togglePiiSelectorDropdown}
          />

          {selectedEntities.length > 0 && (
            <div className="mt-4">
              <h4 className="mb-2 text-xs font-medium text-gray-500">
                Selected Entities
              </h4>
              <PiiTagGroup
                tags={selectedEntities.map((e) => e.name)}
                variant="primary"
                onRemove={(tag) => {
                  const entity = entities.find((e) => e.name === tag);
                  if (entity) {
                    setSelectedEntities(
                      selectedEntities.filter((e) => e.id !== entity.id),
                    );
                  }
                }}
              />
            </div>
          )}

          {selectedEntities.length === 0 && (
            <p className="mt-2 text-sm text-red-500">
              Please select at least one PII type to filter
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-full flex-col md:flex-row md:gap-4">
      {/* Mobile tabs */}
      <div className="border-md-b border-gray-200 p-2 md:hidden dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Secure LLM Chat</h2>
          <div className="flex items-center gap-2">
            <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1.5"
                >
                  <HistoryIcon className="h-4 w-4" />
                  <span className="sr-only md:not-sr-only">History</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-screen max-w-md p-0" align="end">
                <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
                  <h3 className="font-medium">Chat History</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setHistoryOpen(false)}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
                <div className="max-h-[80vh] overflow-y-auto p-4">
                  {renderHistoryContent()}
                </div>
              </PopoverContent>
            </Popover>

            <Popover open={preferencesOpen} onOpenChange={setPreferencesOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1.5"
                >
                  <Settings2Icon className="h-4 w-4" />
                  <span className="sr-only md:not-sr-only">Settings</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-screen max-w-md p-0" align="end">
                <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
                  <h3 className="font-medium">Preferences</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreferencesOpen(false)}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
                <div className="max-h-[80vh] overflow-y-auto p-4">
                  {renderPreferencesContent()}
                </div>
              </PopoverContent>
            </Popover>

            <Button
              size="sm"
              variant="outline"
              className="flex items-center gap-1.5 border-pink-500 text-pink-500 hover:border-pink-500 hover:text-pink-500"
              onClick={openNewChat}
            >
              <MessageSquarePlus className="h-4 w-4" />
              <span className="sr-only md:not-sr-only">New</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <div className="flex h-full flex-col">
          <div className="hidden border-b border-gray-200 bg-white p-4 md:block dark:border-gray-700 dark:bg-black dark:text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">Secure LLM Chat</h2>
                <p className="text-sm text-gray-500">
                  Chat safely with AI, masking sensitive data.
                </p>
              </div>

              {/* <Button
                variant="outline"
                className="border-pink-500 text-pink-500 hover:border-pink-500 hover:text-pink-500"
                onClick={openNewChat}
              >
                <MessageSquarePlus className="mr-2 h-4 w-4" />
                New Chat
              </Button> */}
            </div>
          </div>
          <Body
            chatHistory={chatHistory}
            chatBoxRef={chatBoxRef}
            isLoading={isConversationLoading}
            occuredEntities={occuredEntities}
            viewMode={viewMode}
          />

          <Footer
            handleTextArea={handleTextArea}
            currentMessage={currentMessage}
            loading={loading}
            sendText={sendText}
            redactText={redactText}
            redactedText={redactedText}
            redacted={redacted}
            setRedacted={setRedacted}
            changedValues={[]}
            setChangedValues={() => {}}
            setRedactedText={setRedactedText}
            addFileToChat={addFileToChat}
            setError={setError}
            error={error}
            // TODO: get @jmlhsynv to handle missing plan info

            viewMode={viewMode}
            toggleViewMode={toggleViewMode}
            isNewChat={
              !chats.some((chat: Chat) => chat.id === currentChatId) ||
              chats.some(
                (chat: Chat) =>
                  chat.id === currentChatId && chat.title.includes("New chat"),
              )
            }
            occuredEntitiesLength={occuredEntities.length}
          />
        </div>
      </div>

      {/* Sidebar - hidden on mobile */}
      <div className="hidden h-full w-96 overflow-hidden rounded-lg border border-gray-200 bg-white md:block dark:border-gray-700 dark:bg-black dark:text-white">
        <Tabs defaultValue="history" className="flex h-screen flex-col">
          <div className="border-b border-gray-200 p-4 dark:border-gray-700">
            <TabsList className="grid h-10 w-full grid-cols-2 border border-gray-200 bg-gray-100 p-0 dark:border-gray-700 dark:bg-gray-800">
              <TabsTrigger
                value="history"
                className="flex cursor-pointer items-center gap-2"
              >
                <HistoryIcon className="h-4 w-4" />
                Chat History
              </TabsTrigger>
              <TabsTrigger
                value="preferences"
                className="flex cursor-pointer items-center gap-2"
              >
                <Settings2Icon className="h-4 w-4" />
                Preferences
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="history" className="h-full flex-1 p-4">
            <div className="h-[calc(100vh-14rem)] w-full overflow-y-auto">
              {renderHistoryContent()}
            </div>
          </TabsContent>
          <TabsContent value="preferences" className="flex-1 overflow-auto p-4">
            {renderPreferencesContent()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
